import { useEffect, useState } from 'react';
import { loadRetention } from '../../lib/retentionService';
import AccessDenied from './AccessDenied';
import {
  decliningTenants,
  sortByDrop,
  changeLabel,
  retentionKpis,
  RETENTION_LABELS,
  type NetworkRetention,
  type RetentionStatus,
} from '../../lib/retention';

/** Sections du super admin vers lesquelles les outils de rétention renvoient. */
type SectionTarget = 'sa-services' | 'sa-customers' | 'sa-performance' | 'sa-whatsapp';

/**
 * Section « Rétention & alertes » du SuperAdmin (brique F) : repère les restos en baisse
 * (commandes vs période précédente) et la base fidèle / dormante à relancer.
 * Les outils renvoient vers la section qui permet de passer à l'action.
 */
const WINDOWS = [7, 30, 90];

const STATUS_STYLE: Record<RetentionStatus, string> = {
  en_baisse: 'bg-red-100 text-red-800',
  en_hausse: 'bg-green-100 text-green-800',
  stable: 'bg-slate-100 text-slate-600',
  nouveau: 'bg-blue-100 text-blue-800',
  inactif: 'bg-slate-100 text-slate-400',
};

export default function RetentionSection({
  onOpenSection,
}: {
  onOpenSection?: (id: SectionTarget) => void;
}) {
  const [days, setDays] = useState(30);
  const [ret, setRet] = useState<NetworkRetention | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    loadRetention(days).then((res) => {
      if (res.ok) setRet(res.data);
      else setError(res.error ?? 'Chargement impossible.');
      setLoading(false);
    });
  }, [days]);

  if (error) {
    return <AccessDenied title="Rétention & alertes" sqlFile="sql/27_retention.sql" />;
  }

  const tenants = ret?.tenants ?? [];
  const declining = decliningTenants(tenants);
  const kpis = retentionKpis(ret);

  return (
    <div className="max-w-4xl">
      <div
        className="rounded-xl p-4 mb-4 text-white"
        style={{ background: 'linear-gradient(135deg, #7C3AED 0%, #2563EB 100%)' }}
      >
        <div className="text-xs uppercase font-bold opacity-80">💎 Rétention & fidélisation</div>
        <div className="text-lg font-extrabold">Garde tes clients · réveille les dormants</div>
        <div className="text-sm opacity-90">Détection automatique de baisse sur la fenêtre choisie.</div>
      </div>

      <div className="flex items-center justify-end mb-3 gap-1">
        {WINDOWS.map((w) => (
          <button
            key={w}
            onClick={() => setDays(w)}
            className={'text-sm rounded-full px-3 py-1 border ' + (w === days ? 'text-white border-transparent' : 'opacity-70')}
            style={w === days ? { background: '#7C3AED' } : {}}
          >
            {w} j
          </button>
        ))}
      </div>

      {loading ? (
        <p className="opacity-70">Analyse de la rétention…</p>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
            <Kpi label="📉 Restos en baisse > 20%" value={String(kpis.declining)} tone={kpis.declining ? 'bad' : 'good'} />
            <Kpi label="📈 Restos en hausse" value={String(kpis.rising)} tone="good" />
            <Kpi label="🌟 Clients fidèles (3+ cmd)" value={String(kpis.loyal)} tone="good" />
            <Kpi label="🌙 Clients dormants (>60j)" value={String(kpis.dormant)} tone={kpis.dormant ? 'warn' : 'neutral'} />
          </div>

          {declining.length === 0 ? (
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 text-green-800 text-sm mb-4">
              ✓ Aucun resto en baisse sur la période. Surveillance automatique active.
            </div>
          ) : (
            <div className="rounded-lg bg-amber-50 border border-amber-200 p-4 text-amber-900 text-sm mb-4">
              ⚠️ {declining.length} resto(s) en baisse à relancer : {declining.map((t) => t.name).join(', ')}.
            </div>
          )}

          <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase opacity-60">
                <tr>
                  <th className="px-3 py-2">Restaurant</th>
                  <th className="px-3 py-2 text-right">Cmd (période)</th>
                  <th className="px-3 py-2 text-right">Cmd (précédente)</th>
                  <th className="px-3 py-2 text-right">Évolution</th>
                  <th className="px-3 py-2 text-center">Statut</th>
                </tr>
              </thead>
              <tbody>
                {sortByDrop(tenants).map((t) => (
                  <tr key={t.tenantId} className="border-t border-slate-100">
                    <td className="px-3 py-2 font-medium">
                      {t.name} <span className="opacity-50 text-xs">/{t.slug}</span>
                    </td>
                    <td className="px-3 py-2 text-right">{t.ordersCurrent}</td>
                    <td className="px-3 py-2 text-right">{t.ordersPrevious}</td>
                    <td
                      className="px-3 py-2 text-right font-medium"
                      style={{ color: t.status === 'en_baisse' ? '#DC2626' : t.status === 'en_hausse' ? '#16A34A' : '#64748B' }}
                    >
                      {changeLabel(t)}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <span className={'text-xs rounded-full px-2 py-0.5 ' + STATUS_STYLE[t.status]}>
                        {RETENTION_LABELS[t.status]}
                      </span>
                    </td>
                  </tr>
                ))}
                {tenants.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-3 py-4 text-center opacity-50">
                      Aucune donnée sur la période.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Outils de rétention à activer (parité legacy) */}
          <div className="mt-5">
            <h2 className="mb-2 text-base font-bold">🛠 Outils de rétention à activer</h2>
            <div className="flex flex-col gap-2">
              {RETENTION_TOOLS.map((tool) => (
                <div key={tool.title} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-3">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{tool.icon}</span>
                    <div>
                      <div className="font-semibold">{tool.title}</div>
                      <div className="text-sm text-slate-500">{tool.desc}</div>
                    </div>
                  </div>
                  <button
                    onClick={() => onOpenSection?.(tool.target)}
                    disabled={!onOpenSection}
                    className="shrink-0 rounded-lg px-3 py-1.5 text-sm font-semibold text-white disabled:opacity-40"
                    style={{ background: '#2563EB' }}
                  >
                    {tool.cta}
                  </button>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

const RETENTION_TOOLS: { icon: string; title: string; desc: string; cta: string; target: SectionTarget }[] = [
  { icon: '💬', title: 'NPS automatique post-commande', desc: 'SMS ou WhatsApp 24h après le retrait : « Sur 10, vous nous mettez combien ? » Détection des clients insatisfaits avant qu\'ils partent ailleurs.', cta: 'Activer (Options & services)', target: 'sa-services' },
  { icon: '📊', title: 'Comparatif mensuel envoyé aux restos', desc: 'PDF auto chaque 1er du mois : « {resto} vs moyenne réseau ». Ticket moyen, % paiement en ligne, taux fidélité. Démontre la valeur de Lil & Co.', cta: 'Voir Performance', target: 'sa-performance' },
  { icon: '🎁', title: 'Auto-relance clients dormants', desc: 'Un client n\'a pas commandé depuis 60j → email/WhatsApp auto au nom du resto : « On vous a manqué ? -2 € sur votre prochaine commande ».', cta: 'Ouvrir le CRM', target: 'sa-customers' },
  { icon: '🏆', title: 'Programme fidélité réseau', desc: 'Carte virtuelle Lil & Co : 10e burger offert quel que soit le resto. Augmente la LTV client + la valeur perçue par les partenaires.', cta: 'Activer la fidélité', target: 'sa-services' },
];

function Kpi({ label, value, tone }: { label: string; value: string; tone: 'good' | 'warn' | 'bad' | 'neutral' }) {
  const color =
    tone === 'bad' ? '#DC2626' : tone === 'warn' ? '#D97706' : tone === 'good' ? '#16A34A' : '#0F172A';
  return (
    <div className="rounded-lg bg-white border border-slate-200 p-3">
      <div className="text-xs uppercase opacity-50">{label}</div>
      <div className="text-2xl font-extrabold" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
